import React, { useState } from "react";
import { Box, Typography, Card, CardContent, IconButton, Avatar, Stack, Menu, MenuItem } from "@mui/material";
import LogoutIcon from "@mui/icons-material/Logout";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CloseIcon from "@mui/icons-material/Close";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import BuildIdLogo from "../../assets/images/build_id_logo.png";
import { BIGrey } from "../../assets/helpers";
import AccessTimeIcon from "@mui/icons-material/AccessTime";

export const SelectOrganizationContent = ({
  clients,
  pendingInvitations,
  onSelectClient,
  onViewInvite,
  onLogout,
  onClose,
  showLogout
}) => {
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const showMoreMenu = (clients || []).some((c) => c.HideBiFree === false);

  return (
    <div style={{ minHeight: "300px" }}>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          backgroundColor: "#fff",
          borderBottom: "1px solid #eee"
        }}>
        <img src={BuildIdLogo} alt="Build ID" style={{ height: "30px", objectFit: "contain" }} />
        {showLogout ? (
          <Stack direction="row" alignItems="center">
            {showMoreMenu && (
              <IconButton onClick={(e) => setMenuAnchorEl(e.currentTarget)} aria-label="more">
                <MoreVertIcon sx={{ color: "#000" }} />
              </IconButton>
            )}
            <IconButton onClick={onLogout} aria-label="logout">
              <LogoutIcon sx={{ color: "#000" }} />
            </IconButton>
          </Stack>
        ) : onClose ? (
          <Stack direction="row" alignItems="center">
            {showMoreMenu && (
              <IconButton onClick={(e) => setMenuAnchorEl(e.currentTarget)} aria-label="more">
                <MoreVertIcon sx={{ color: "#000" }} />
              </IconButton>
            )}
            <IconButton onClick={onClose} aria-label="close">
              <CloseIcon sx={{ color: "#000" }} />
            </IconButton>
          </Stack>
        ) : null}
      </Box>

      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={() => setMenuAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MenuItem onClick={() => { setMenuAnchorEl(null); onSelectClient({ ClientId: 0 }); }}>
          <img src={BuildIdLogo} alt="Build ID" style={{ height: "18px", marginRight: "8px", objectFit: "contain" }} />
          Go to Build ID
        </MenuItem>
      </Menu>

      {/* Main Content */}
      <Box>
        <Typography
          variant="h5"
          sx={{
            fontWeight: "bold",
            color: "#0B2B5B",
            textAlign: "center",
            marginBottom: "8px"
          }}>
          Select Your Organization
        </Typography>
        <Typography
          variant="body1"
          sx={{
            color: BIGrey,
            textAlign: "center",
            marginBottom: "32px"
          }}>
          Choose an organization to continue
        </Typography>

        {/* Pending Invitations Section */}
        {pendingInvitations && pendingInvitations.length > 0 && (
          <Box sx={{ marginTop: "24px", paddingRight: "16px" }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: "bold",
                color: "#0B2B5B",
                textAlign: "left",
                marginBottom: "12px",
                marginLeft: "4px"
              }}>
              Pending Invitations
            </Typography>
            <Stack spacing={2} sx={{ marginBottom: "24px" }}>
              {pendingInvitations.map((invite, index) => (
                <Card
                  key={`invite-${index}`}
                  onClick={() => onViewInvite(invite)}
                  sx={{
                    borderRadius: "16px",
                    boxShadow: "0px 4px 12px rgba(0,0,0,0.05)",
                    cursor: "pointer",
                    position: "relative",
                    overflow: "hidden",
                    borderLeft: "5px solid orange", // Distinct color for invites
                    transition: "transform 0.2s",
                    "&:hover": {
                      transform: "scale(1.02)"
                    }
                  }}>
                  <CardContent
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      padding: "16px !important",
                      zIndex: 1,
                      position: "relative"
                    }}>
                    {/* Avatar with Logo or Placeholder */}
                    <Avatar
                      src={invite.ClientLogo || invite.Logo || ""}
                      alt={invite.OrganizationName || invite.ClientName || "Invitation"}
                      sx={{
                        width: 60,
                        height: 60,
                        marginRight: "16px",
                        bgcolor: invite.ClientLogo || invite.Logo ? "white" : "orange",
                        border: "1px solid #eee",
                        color: "white",
                        fontWeight: "bold",
                        img: { objectFit: "contain" }
                      }}>
                      {!invite.ClientLogo &&
                        !invite.Logo &&
                        (invite.OrganizationName?.[0] || invite.ClientName?.[0] || "Inv")}
                    </Avatar>

                    <Box sx={{ flexGrow: 1 }}>
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: "bold",
                          fontSize: "1rem",
                          color: "#0B2B5B",
                          marginBottom: "4px"
                        }}>
                        {invite.OrganizationName ||
                          invite.ClientName ||
                          `Invitation to Join Organization ${invite.ClientId}`}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Status: {invite?.InvitationStatus || "Invited"}
                      </Typography>
                    </Box>

                    <Box
                      sx={{
                        width: "40px",
                        height: "40px",
                        borderRadius: "50%",
                        backgroundColor: "#FFF3E0", // Light orange bg
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        marginLeft: "12px"
                      }}>
                      <ChevronRightIcon sx={{ color: "#E65100" }} />
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Stack>
          </Box>
        )}

        <Stack spacing={2}>
           <Typography
              variant="h6"
              sx={{
                fontWeight: "bold",
                color: "#0B2B5B",
                textAlign: "left",
                marginBottom: "12px",
                marginLeft: "4px"
              }}>
              Current Clients
            </Typography>
          {clients.map((client, index) => (
            <Card
              key={index}
              onClick={() => onSelectClient(client)}
              sx={{
                borderRadius: "16px",
                boxShadow: "0px 4px 12px rgba(0,0,0,0.05)",
                cursor: "pointer",
                position: "relative",
                overflow: "hidden",
                borderLeft: index === 1 ? "5px solid blue" : index === 2 ? "5px solid red" : "none",
                transition: "transform 0.2s",
                "&:hover": {
                  transform: "scale(1.02)"
                }
              }}>
              <CardContent
                sx={{
                  display: "flex",
                  alignItems: "center",
                  padding: "16px !important",
                  zIndex: 1,
                  position: "relative"
                }}>
                <Avatar
                  src={client.ClientLogo || ""}
                  alt={client.OrganizationName}
                  sx={{
                    width: 60,
                    height: 60,
                    marginRight: "16px",
                    bgcolor: "white",
                    border: "1px solid #eee",
                    img: { objectFit: "contain" }
                  }}>
                  {!client.ClientLogo && (client.OrganizationName?.[0] || "C")}
                </Avatar>

                <Box sx={{ flexGrow: 1 }}>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: "bold",
                      fontSize: "1rem",
                      color: "#0B2B5B",
                      marginBottom: "4px"
                    }}>
                    {client.OrganizationName}
                  </Typography>

                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={0.5}
                    sx={{ marginBottom: "8px" }}>
                    <AccessTimeIcon sx={{ fontSize: "14px", color: BIGrey }} />
                    <Typography variant="caption" sx={{ color: BIGrey }}>
                      {index === 0 ? "Today" : index === 1 ? "1 weeks ago" : "3 weeks ago"}
                    </Typography>
                  </Stack>

                  <div
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      backgroundColor: "#FFF9E6",
                      border: "1px solid #FFE4A0",
                      borderRadius: "12px",
                      padding: "4px 8px"
                    }}>
                    <div
                      style={{
                        width: "12px",
                        height: "8px",
                        backgroundColor: "#FFB000",
                        borderRadius: "2px",
                        marginRight: "6px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center"
                      }}>
                      <div style={{ width: "6px", height: "2px", background: "black" }}></div>
                    </div>
                    <Typography
                      variant="caption"
                      sx={{
                        color: "#B28C00",
                        fontWeight: "bold",
                        fontSize: "0.7rem"
                      }}>
                      {!client?.HideBiFree
                        ? "Build ID Access Available"
                        : "Build ID Access Not Available"}
                    </Typography>
                  </div>
                </Box>

                <Box
                  sx={{
                    width: "40px",
                    height: "40px",
                    borderRadius: "50%",
                    backgroundColor: index % 2 === 0 ? "#f0f0f0" : "#e0e7ff",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    marginLeft: "12px"
                  }}>
                  <ChevronRightIcon sx={{ color: "#000" }} />
                </Box>
              </CardContent>
            </Card>
          ))}
        </Stack>
      </Box>

      
    </div>
  );
};
